import { Controller, type Control, type FieldPath, type FieldValues } from 'react-hook-form';

import { FormDateInput, type FormDateInputProps } from './form-date-input';

export type ControlledDateInputProps<T extends FieldValues> = Omit<
  FormDateInputProps,
  'value' | 'onDateChange' | 'status' | 'errorMessage'
> & {
  control: Control<T>;
  name: FieldPath<T>;
};

/**
 * `FormDateInput` pre-wired to a react-hook-form field. Pass `control` + `name` instead of
 * repeating a `<Controller>` with value/onDateChange/status/errorMessage at every call site.
 */
export function ControlledDateInput<T extends FieldValues>({
  control,
  name,
  ...rest
}: ControlledDateInputProps<T>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState: { error } }) => (
        <FormDateInput
          {...rest}
          value={(value as Date | null | undefined) ?? null}
          onDateChange={onChange}
          status={error?.message ? 'error' : 'default'}
          errorMessage={error?.message}
        />
      )}
    />
  );
}

export default ControlledDateInput;
